import { Controller } from "@hotwired/stimulus";
import { animate } from "animejs";

export default class DropdownController extends Controller {
  static targets = ["panel"];
  static values = {
    popover: Boolean,
    anchor: { type: String, default: "right" },
    offset: { type: Number, default: 4 },
  };

  get isDetails() {
    return this.element.tagName === "DETAILS";
  }
  get isOpen() {
    if (this.popoverValue) return this.panelTarget.matches(":popover-open");
    return this.isDetails ? this.element.open : this.element.classList.contains("sc-dropdown-open");
  }

  connect() {
    this._onDocumentClick = (e) => {
      if (this.isOpen && !this.element.contains(e.target)) this.close();
    };
    this._onBeforeCache = () => this._forceClose();
    this._onKeydown = (e) => {
      if (e.key === "Escape" && this.isOpen) this.close();
    };
    this._onSidebarCollapse = () => this.close();
    document.addEventListener("click", this._onDocumentClick);
    document.addEventListener("turbo:before-cache", this._onBeforeCache);
    document.addEventListener("keydown", this._onKeydown);
    document.addEventListener("sidebar:collapse", this._onSidebarCollapse);
  }

  disconnect() {
    document.removeEventListener("click", this._onDocumentClick);
    document.removeEventListener("turbo:before-cache", this._onBeforeCache);
    document.removeEventListener("keydown", this._onKeydown);
    document.removeEventListener("sidebar:collapse", this._onSidebarCollapse);
  }

  toggle(event) {
    if (this.isOpen) {
      event.preventDefault();
      this._animateClose();
      return;
    }
    this.open();
  }

  open() {
    if (this.isOpen) return;
    if (this.popoverValue) {
      this.panelTarget.showPopover();
      this._positionPopover();
    } else if (!this.isDetails) {
      this.element.classList.add("sc-dropdown-open");
    }
    // details: browser handles open natively; @starting-style animates in
    const trigger = this._trigger();
    if (trigger) trigger.setAttribute("aria-expanded", "true");
    // Load any lazy turbo-frame inside the panel on first open
    const lazyFrame = this.panelTarget.querySelector("turbo-frame[data-lazy-src]:not([src])");
    if (lazyFrame) lazyFrame.setAttribute("src", lazyFrame.dataset.lazySrc);
  }

  _positionPopover() {
    const panel = this.panelTarget;
    const trigger = this.element.querySelector("button") || this.element;
    const rect = trigger.getBoundingClientRect();
    if (this.anchorValue === "inline-right") {
      panel.style.top = `${rect.top + rect.height / 2 - 16}px`;
      panel.style.left = `${rect.right + (this.offsetValue || 8)}px`;
      panel.style.right = "auto";
    } else {
      panel.style.top = `${rect.bottom + this.offsetValue}px`;
      if (this.anchorValue === "left") {
        panel.style.left = `${rect.left}px`;
        panel.style.right = "auto";
      } else {
        panel.style.right = `${window.innerWidth - rect.right}px`;
        panel.style.left = "auto";
      }
    }
  }

  close() {
    if (this.isOpen && !this._closing) this._animateClose();
  }

  _forceClose() {
    if (this.popoverValue) {
      this.panelTarget.hidePopover();
    } else if (this.isDetails) {
      this.element.removeAttribute("open");
    } else {
      this.element.classList.remove("sc-dropdown-open");
    }
    const trigger = this._trigger();
    if (trigger) trigger.setAttribute("aria-expanded", "false");
  }

  _animateClose() {
    this._closing = true;
    const panel = this.panelTarget;
    animate(panel, {
      opacity: 0,
      scale: 0.97,
      translateY: "-4px",
      duration: 150,
      ease: "outQuad",
      onComplete: () => {
        this._closing = false;
        panel.style.opacity = "";
        panel.style.transform = "";
        if (this.popoverValue) {
          panel.hidePopover();
        } else if (this.isDetails) {
          this.element.removeAttribute("open");
        } else {
          this.element.classList.remove("sc-dropdown-open");
        }
        const trigger = this._trigger();
        if (trigger) trigger.setAttribute("aria-expanded", "false");
      },
    });
  }

  _trigger() {
    if (this.isDetails) return null;
    return (
      this.element.querySelector(":scope > button") ||
      this.element.querySelector("button") ||
      this.element
    );
  }
}
